/**
 * DecisionDrawer
 * 
 * A slide-out drawer for Exception Radar that shows detailed decision context.
 * Tabs: Trace | Decision Pack | Outcomes | What-If | Trajectory
 * 
 * Integrates with Context Graph v2 APIs to display:
 * - Decision Pack (precedents, costs, confidence, policy constraints)
 * - Outcomes summary
 * - What-If simulation results
 * - Trajectory steps
 */

import React, { useState, useEffect } from 'react';
import { getDecisionPack } from '../api/policies';
import apiClient from '../services/api';
import { toast } from 'sonner';
import PoliciesQuickStartModal from './PoliciesQuickStartModal';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  FileText,
  Package,
  TrendingUp,
  GitBranch,
  Sparkles,
  ExternalLink,
  Copy,
  CheckCircle,
  AlertTriangle,
  Clock,
  DollarSign,
  ShieldAlert,
  Settings,
  Zap
} from 'lucide-react';

const ADMIN_TOKEN = 'dev';

export default function DecisionDrawer({ open, onClose, trace, brandId }) {
  const [activeTab, setActiveTab] = useState('trace');
  const [decisionPack, setDecisionPack] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [whatIf, setWhatIf] = useState(null);
  const [trajectory, setTrajectory] = useState(null);
  const [loading, setLoading] = useState({});
  const [policyQuickStartOpen, setPolicyQuickStartOpen] = useState(false);

  const traceId = trace?.trace_id;

  // Fetch Decision Pack when tab selected
  useEffect(() => {
    if (!open || !traceId || activeTab !== 'decision-pack') return;
    if (decisionPack) return; // Already loaded

    setLoading(prev => ({ ...prev, decisionPack: true }));
    getDecisionPack(brandId, traceId)
      .then(data => setDecisionPack(data))
      .catch(err => {
        console.error('Failed to load Decision Pack:', err);
        setDecisionPack({ error: true });
      })
      .finally(() => setLoading(prev => ({ ...prev, decisionPack: false })));
  }, [open, traceId, activeTab, brandId, decisionPack]);

  // Fetch Outcomes when tab selected
  useEffect(() => {
    if (!open || !traceId || activeTab !== 'outcomes') return;
    if (outcomes) return;

    setLoading(prev => ({ ...prev, outcomes: true }));
    apiClient.get(`/api/v1/decision-graph/outcomes?brand_id=${brandId}&trace_id=${traceId}&window_days=90`, {
      headers: { 'X-Admin-Token': ADMIN_TOKEN }
    })
      .then(res => setOutcomes(res.data))
      .catch(err => {
        console.error('Failed to load outcomes:', err);
        setOutcomes({ error: true });
      })
      .finally(() => setLoading(prev => ({ ...prev, outcomes: false })));
  }, [open, traceId, activeTab, brandId, outcomes]);

  // Fetch Trajectory when tab selected
  useEffect(() => {
    if (!open || !traceId || activeTab !== 'trajectory') return;
    if (trajectory) return;

    setLoading(prev => ({ ...prev, trajectory: true }));
    apiClient.get(`/api/v1/decision-graph/trajectories?brand_id=${brandId}&trace_id=${traceId}`, {
      headers: { 'X-Admin-Token': ADMIN_TOKEN }
    })
      .then(res => setTrajectory(res.data))
      .catch(err => {
        console.error('Failed to load trajectory:', err);
        setTrajectory({ error: true });
      })
      .finally(() => setLoading(prev => ({ ...prev, trajectory: false })));
  }, [open, traceId, activeTab, brandId, trajectory]);

  // Fetch What-If simulation when tab selected
  useEffect(() => {
    if (!open || !traceId || activeTab !== 'what-if') return;
    if (whatIf) return;

    // We need to get trace info to run what-if simulation
    if (!trace?.object_ref?.id) return;

    setLoading(prev => ({ ...prev, whatIf: true }));
    
    // Call the what-if endpoint with a default ship strategy simulation
    apiClient.post(`/api/v1/decision-graph/what-if/ship-strategy`, {
      brand_id: brandId,
      order_id: trace.object_ref?.id || 'unknown',
      strategies: ['expedite', 'split_ship', 'delay_to_complete']
    }, {
      headers: { 'X-Admin-Token': ADMIN_TOKEN }
    })
      .then(res => setWhatIf(res.data))
      .catch(err => {
        console.error('Failed to load What-If simulation:', err);
        setWhatIf({ error: true, message: err.message });
      })
      .finally(() => setLoading(prev => ({ ...prev, whatIf: false })));
  }, [open, traceId, activeTab, brandId, whatIf, trace]);

  // Reset state when drawer closes or trace changes
  useEffect(() => {
    if (!open) {
      setDecisionPack(null);
      setOutcomes(null);
      setWhatIf(null);
      setTrajectory(null);
      setActiveTab('trace');
    }
  }, [open, traceId]);

  // Copy trace ID to clipboard
  const copyTraceId = () => {
    navigator.clipboard.writeText(traceId || '');
    toast.success('Trace ID copied to clipboard');
  };

  // Check if policies are missing
  const policiesMissing = !decisionPack?.policy_constraints || 
    Object.keys(decisionPack.policy_constraints).length === 0;

  // Format money
  const fmtMoney = (val) => {
    if (val === null || val === undefined) return '—';
    return `$${Math.round(val)}`;
  };

  // Get confidence badge color
  const getConfidenceColor = (level) => {
    switch (level) {
      case 'high': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
      case 'med': return 'bg-amber-400/10 text-amber-400 border-amber-400/30';
      case 'low': return 'bg-red-400/10 text-red-400 border-red-400/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  // Get freshness badge color
  const getFreshnessBadgeColor = (badge) => {
    switch (badge) {
      case 'Fresh': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
      case 'Aging': return 'bg-amber-400/10 text-amber-400 border-amber-400/30';
      case 'Stale': return 'bg-red-400/10 text-red-400 border-red-400/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (!trace) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader className="pb-4 border-b border-border">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <SheetTitle className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  {trace.action_taken || 'Decision Trace'}
                </SheetTitle>
                <SheetDescription className="mt-1">
                  {trace.object_ref?.type}: {trace.object_ref?.id || 'Unknown'}
                </SheetDescription>
              </div>
              <div className="flex items-center gap-2">
                {/* Approval state badge */}
                {trace.committed_at && (
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Committed
                  </Badge>
                )}
                {trace.approval_channel === 'slack' && !trace.committed_at && (
                  <Badge className="bg-amber-400/10 text-amber-400 border-amber-400/30">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending
                  </Badge>
                )}
                {/* Policy gate badge */}
                {trace.metadata?.policy_gate?.reason && (
                  <Badge className="bg-amber-400/10 text-amber-400 border-amber-400/30">
                    <ShieldAlert className="h-3 w-3 mr-1" />
                    Policy Gated
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Quick actions */}
            <div className="flex items-center gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={copyTraceId}>
                <Copy className="h-3.5 w-3.5 mr-1.5" />
                Copy ID
              </Button>
            </div>
          </SheetHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="trace" className="text-xs">
                <FileText className="h-3.5 w-3.5 mr-1.5" />
                Trace
              </TabsTrigger>
              <TabsTrigger value="decision-pack" className="text-xs">
                <Package className="h-3.5 w-3.5 mr-1.5" />
                Decision Pack
              </TabsTrigger>
              <TabsTrigger value="outcomes" className="text-xs">
                <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                Outcomes
              </TabsTrigger>
              <TabsTrigger value="what-if" className="text-xs">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                What-If
              </TabsTrigger>
              <TabsTrigger value="trajectory" className="text-xs">
                <GitBranch className="h-3.5 w-3.5 mr-1.5" />
                Trajectory
              </TabsTrigger>
            </TabsList>

            {/* Trace Tab */}
            <TabsContent value="trace" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Decision Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-muted-foreground">Decision Type</div>
                    <div className="font-medium">{trace.decision_type}</div>
                    
                    <div className="text-muted-foreground">Action Taken</div>
                    <div className="font-medium">{trace.action_taken}</div>
                    
                    <div className="text-muted-foreground">Created</div>
                    <div className="font-medium">
                      {trace.created_at ? new Date(trace.created_at).toLocaleString() : '—'}
                    </div>
                    
                    {trace.approver && (
                      <>
                        <div className="text-muted-foreground">Approver</div>
                        <div className="font-medium">{trace.approver.name || trace.approver.user_id}</div>
                      </>
                    )}
                  </div>

                  {trace.reason_codes?.length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Reason Codes</p>
                      <div className="flex flex-wrap gap-1">
                        {trace.reason_codes.map((code, idx) => (
                          <Badge key={idx} variant="outline">{code}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {trace.rationale_text && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Rationale</p>
                      <p className="text-sm">{trace.rationale_text}</p>
                    </div>
                  )}

                  {/* Policy gate info */}
                  {trace.metadata?.policy_gate?.reason && (
                    <div className="rounded-xl bg-amber-400/10 border border-amber-400/30 p-3">
                      <div className="flex items-center gap-2 text-amber-400 font-semibold text-sm">
                        <ShieldAlert className="h-4 w-4" />
                        Policy gate triggered
                      </div>
                      <p className="text-sm text-amber-100/80 mt-1">
                        {trace.metadata.policy_gate.reason}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Decision Pack Tab */}
            <TabsContent value="decision-pack" className="mt-4 space-y-4">
              {loading.decisionPack ? (
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : decisionPack?.error ? (
                <Card>
                  <CardContent className="p-6 text-center text-muted-foreground">
                    Failed to load Decision Pack. Please try again.
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Policy constraints missing warning */}
                  {policiesMissing && (
                    <div className="rounded-xl bg-amber-400/10 border border-amber-400/30 p-4">
                      <div className="flex items-center gap-2 text-amber-100 font-semibold text-sm">
                        <AlertTriangle className="h-4 w-4 text-amber-400" />
                        Policy profile not configured yet (optional)
                      </div>
                      <p className="text-sm text-amber-100/70 mt-1">
                        Configure minimum thresholds so Decision Pack and Slack approvals become policy-aware.
                      </p>
                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-sky-500/10 border-sky-400/30 hover:bg-sky-500/15 hover:border-sky-400/40"
                          onClick={() => setPolicyQuickStartOpen(true)}
                        >
                          <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                          Policy Quick Start (2 min)
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.location.href = '/dashboard/settings/policies'}
                        >
                          <Settings className="h-3.5 w-3.5 mr-1.5" />
                          Open Policies
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Confidence Card */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Zap className="h-4 w-4 text-primary" />
                        Confidence
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-3">
                        <Badge className={`${getConfidenceColor(decisionPack?.confidence?.level)}`}>
                          {decisionPack?.confidence?.level === 'high' && '✅ '}
                          {decisionPack?.confidence?.level === 'med' && '⚠️ '}
                          {decisionPack?.confidence?.level === 'low' && '❗ '}
                          {decisionPack?.confidence?.level || 'Unknown'} confidence
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {decisionPack?.confidence?.evidence_count || 0} examples
                          {decisionPack?.confidence?.recency_days !== null && 
                            ` • ${decisionPack.confidence.recency_days}d recency`}
                        </span>
                      </div>
                      {decisionPack?.why?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-muted-foreground mb-1">Why:</p>
                          <ul className="text-sm space-y-1">
                            {decisionPack.why.map((reason, idx) => (
                              <li key={idx} className="text-muted-foreground">• {reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Policy Constraints Card */}
                  {!policiesMissing && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <ShieldAlert className="h-4 w-4 text-primary" />
                          Policy Constraints
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2 text-sm">
                          {Object.entries(decisionPack.policy_constraints || {}).slice(0, 3).map(([key, content]) => (
                            <div key={key} className="flex items-center justify-between">
                              <span className="text-muted-foreground">{key}</span>
                              <Badge variant="outline" className="text-xs">
                                v{decisionPack.active_policy_versions?.[key] || '?'}
                              </Badge>
                            </div>
                          ))}
                          {Object.keys(decisionPack.policy_constraints || {}).length > 3 && (
                            <p className="text-xs text-muted-foreground">
                              +{Object.keys(decisionPack.policy_constraints).length - 3} more
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Expected Cost Card */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-primary" />
                        Expected Cost
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-lg font-semibold">
                            {fmtMoney(decisionPack?.expected_cost?.support_cost_estimate)}
                          </p>
                          <p className="text-xs text-muted-foreground">Support</p>
                        </div>
                        <div>
                          <p className="text-lg font-semibold">
                            {fmtMoney(decisionPack?.expected_cost?.margin_risk_estimate)}
                          </p>
                          <p className="text-xs text-muted-foreground">Margin</p>
                        </div>
                        <div>
                          <p className="text-lg font-semibold">
                            {fmtMoney(decisionPack?.expected_cost?.expedite_cost_estimate)}
                          </p>
                          <p className="text-xs text-muted-foreground">Expedite</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Precedents Card */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="h-4 w-4 text-primary" />
                        Top Precedents
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {decisionPack?.top_precedents?.length > 0 ? (
                        <div className="space-y-3">
                          {decisionPack.top_precedents.map((prec, idx) => (
                            <div key={prec.precedent_id} className="flex items-start justify-between p-2 rounded-lg bg-muted/30 border border-border">
                              <div className="flex-1">
                                <p className="text-sm font-medium">{prec.title || 'Untitled'}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Badge className={getFreshnessBadgeColor(prec.freshness_badge)} variant="outline">
                                    {prec.freshness_badge || 'Unknown'}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    conf {(prec.effective_confidence || 0).toFixed(2)}
                                  </span>
                                  {prec.policy_mismatch && (
                                    <Badge variant="outline" className="text-xs bg-amber-400/10 text-amber-400 border-amber-400/30">
                                      policy mismatch
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No precedents yet — this will improve as you capture decisions.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>

            {/* Outcomes Tab */}
            <TabsContent value="outcomes" className="mt-4 space-y-4">
              {loading.outcomes ? (
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : outcomes?.error ? (
                <Card>
                  <CardContent className="p-6 text-center text-muted-foreground">
                    Failed to load outcomes. Please try again.
                  </CardContent>
                </Card>
              ) : outcomes?.outcomes?.length > 0 ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Linked Outcomes</CardTitle>
                    <CardDescription>
                      {outcomes.outcomes.length} outcome(s) linked to this trace
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {outcomes.outcomes.map((outcome, idx) => (
                        <div key={outcome._id || idx} className="p-3 rounded-lg bg-muted/30 border border-border">
                          <div className="flex items-center justify-between">
                            <Badge variant="outline">{outcome.outcome_type || 'Unknown'}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {outcome.observed_at ? new Date(outcome.observed_at).toLocaleDateString() : '—'}
                            </span>
                          </div>
                          {outcome.margin_impact_estimate && (
                            <p className="text-sm mt-2">
                              Margin impact: {fmtMoney(outcome.margin_impact_estimate)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <TrendingUp className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      No outcomes linked yet. Outcomes improve once we ingest support/refund/fulfillment signals.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* What-If Tab */}
            <TabsContent value="what-if" className="mt-4 space-y-4">
              {loading.whatIf ? (
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : whatIf?.error ? (
                <Card>
                  <CardContent className="p-6 text-center text-muted-foreground">
                    <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
                    <p className="text-sm">
                      What-If simulation unavailable. {whatIf.message || 'Please try again.'}
                    </p>
                  </CardContent>
                </Card>
              ) : whatIf?.ok && whatIf?.options ? (
                <div className="space-y-3">
                  {/* Confidence & Drift Warning */}
                  <Card className={whatIf.confidence?.level === 'low' ? 'border-amber-500/30' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge className={getConfidenceColor(whatIf.confidence?.level)}>
                            {whatIf.confidence?.level || 'unknown'} confidence
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {whatIf.confidence?.n_similar || 0} similar examples
                            {whatIf.confidence?.recency_days !== null && 
                              ` • ${whatIf.confidence.recency_days}d recency`}
                          </span>
                        </div>
                      </div>
                      
                      {/* Drift Warning */}
                      {whatIf.confidence?.drift_warning && (
                        <div className="mt-2 p-2 rounded bg-amber-500/10 border border-amber-500/30">
                          <div className="flex items-center gap-2 text-amber-400 text-xs">
                            <AlertTriangle className="h-3 w-3" />
                            Pattern drift detected — recent outcomes differ from historical data
                          </div>
                        </div>
                      )}
                      
                      {/* Low confidence warning */}
                      {whatIf.confidence?.level === 'low' && (
                        <p className="mt-2 text-xs text-amber-400">
                          ⚠️ Estimate only — insufficient data for high-confidence projection
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Strategy Comparison */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-primary" />
                        Ship Strategy Comparison
                      </CardTitle>
                      <CardDescription>
                        Compare outcomes for different fulfillment strategies
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.entries(whatIf.options || {}).map(([strategyKey, option]) => (
                          <div 
                            key={strategyKey} 
                            className="p-3 rounded-lg border bg-muted/30 border-border"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm capitalize">{strategyKey.replace('_', ' ')}</span>
                              {option.expected_cost?.margin && (
                                <span className={`text-sm font-semibold ${option.expected_cost.margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {option.expected_cost.margin >= 0 ? '+' : ''}{fmtMoney(option.expected_cost.margin)}
                                </span>
                              )}
                            </div>
                            {option.probabilities && (
                              <div className="grid grid-cols-4 gap-2 mt-2 text-xs text-muted-foreground">
                                <div>
                                  <span className="block">Late/Partial</span>
                                  <span className="text-foreground">{(option.probabilities.late_partial * 100).toFixed(1)}%</span>
                                </div>
                                <div>
                                  <span className="block">Cancel</span>
                                  <span className="text-foreground">{(option.probabilities.cancellation * 100).toFixed(1)}%</span>
                                </div>
                                <div>
                                  <span className="block">Ticket</span>
                                  <span className="text-foreground">{(option.probabilities.repeat_ticket * 100).toFixed(1)}%</span>
                                </div>
                                <div>
                                  <span className="block">Refund</span>
                                  <span className="text-foreground">{(option.probabilities.refund * 100).toFixed(1)}%</span>
                                </div>
                              </div>
                            )}
                            {option.expected_cost && (
                              <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-muted-foreground">
                                <div>
                                  <span className="block">Support Cost</span>
                                  <span className="text-foreground">{fmtMoney(option.expected_cost.support)}</span>
                                </div>
                                <div>
                                  <span className="block">Expedite</span>
                                  <span className="text-foreground">{fmtMoney(option.expected_cost.expedite)}</span>
                                </div>
                                <div>
                                  <span className="block">Margin</span>
                                  <span className="text-foreground">{fmtMoney(option.expected_cost.margin)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Explanation/Method */}
                  {whatIf.explanation && (
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-xs text-muted-foreground">
                          Method: {whatIf.explanation.method || 'similarity_weighted_empirical'} • 
                          Window: {whatIf.explanation.window_days || 90} days • 
                          {whatIf.confidence?.level !== 'high' && ' Treat as estimates only'}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <Sparkles className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      What-If simulation not available for this trace. 
                      Requires an order reference with fulfillment data.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Trajectory Tab */}
            <TabsContent value="trajectory" className="mt-4 space-y-4">
              {loading.trajectory ? (
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-32 w-full" />
                </div>
              ) : trajectory?.error ? (
                <Card>
                  <CardContent className="p-6 text-center text-muted-foreground">
                    Failed to load trajectory. Please try again.
                  </CardContent>
                </Card>
              ) : trajectory?.trajectories?.length > 0 ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Event Trajectory</CardTitle>
                    <CardDescription>
                      Steps in this decision's lifecycle
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {trajectory.trajectories[0]?.steps?.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-3 pb-3 last:pb-0">
                        <div className="flex flex-col items-center">
                          <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-medium text-primary">
                            {idx + 1}
                          </div>
                          {idx < (trajectory.trajectories[0]?.steps?.length || 0) - 1 && (
                            <div className="w-0.5 h-8 bg-border mt-1" />
                          )}
                        </div>
                        <div className="flex-1 pt-0.5">
                          <p className="text-sm font-medium">{step.step_type || 'Step'}</p>
                          <p className="text-xs text-muted-foreground">
                            {step.timestamp ? new Date(step.timestamp).toLocaleString() : '—'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-6 text-center">
                    <GitBranch className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Trajectory not generated yet.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Policy Quick Start Modal */}
      <PoliciesQuickStartModal
        open={policyQuickStartOpen}
        onClose={() => setPolicyQuickStartOpen(false)}
        brandId={brandId}
        onDone={() => {
          // Refresh Decision Pack after policies created
          setDecisionPack(null);
          if (activeTab === 'decision-pack') {
            getDecisionPack(brandId, traceId)
              .then(data => setDecisionPack(data))
              .catch(err => setDecisionPack({ error: true }));
          }
        }}
      />
    </>
  );
}
